import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Plugin Hook System
 * 
 * Allows plugins to inject custom components at specific locations in the UI.
 * 
 * Usage:
 * 1. Register hooks: registerHook('topbar.right', MyComponent)
 * 2. Render hooks: const components = useHookSlot('topbar.right')
 * 
 * Available hook locations:
 * - topbar.left, topbar.center, topbar.right
 * - sidebar.top, sidebar.menu.before, sidebar.menu.after, sidebar.bottom
 * - day.header.before, day.header.after
 * - reservation.card.before, reservation.card.after, reservation.card.actions
 * - reservation.modal.header, reservation.modal.tabs, reservation.modal.footer
 * - clients.list.item
 * - admin.tabs
 */

const PluginHooksContext = createContext(null);

export const PluginHooksProvider = ({ children }) => {
    const [hooks, setHooks] = useState({});

    const registerHook = useCallback((location, component, priority = 10) => {
        setHooks(prev => {
            const locationHooks = prev[location] || [];
            const newHook = { component, priority, id: Date.now() + Math.random() };
            const updated = [...locationHooks, newHook].sort((a, b) => a.priority - b.priority);
            return { ...prev, [location]: updated };
        });
    }, []);

    const unregisterHook = useCallback((location, hookId) => {
        setHooks(prev => {
            const locationHooks = prev[location] || [];
            const updated = locationHooks.filter(h => h.id !== hookId);
            return { ...prev, [location]: updated };
        });
    }, []);

    const getHooks = useCallback((location) => {
        return hooks[location] || [];
    }, [hooks]);

    return (
        <PluginHooksContext.Provider value={{ registerHook, unregisterHook, getHooks }}>
            {children}
        </PluginHooksContext.Provider>
    );
};

export const usePluginHooks = () => {
    const context = useContext(PluginHooksContext);
    if (!context) {
        throw new Error('usePluginHooks must be used within PluginHooksProvider');
    }
    return context;
};

/**
 * Hook to render components registered at a specific location
 * @param {string} location - The hook location identifier
 * @param {object} props - Props to pass to hooked components
 * @returns {JSX.Element[]} Array of rendered hook components
 */
export const useHookSlot = (location, props = {}) => {
    const { getHooks } = usePluginHooks();
    const locationHooks = getHooks(location);

    return locationHooks.map(({ component: Component, id }) => (
        <Component key={id} {...props} />
    ));
};

/**
 * Component to render a hook slot in the UI
 */
export const HookSlot = ({ location, className = '', ...props }) => {
    const components = useHookSlot(location, props);

    if (components.length === 0) return null;

    return (
        <div className={`hook-slot hook-slot-${location.replace(/\./g, '-')} ${className}`}>
            {components}
        </div>
    );
};
